import React, { useState } from "react";
import { Info } from "lucide-react";
import { useParams } from "react-router-dom";

import ButtonV2 from "../../../components/ui/button/v2";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { GeneratedFraudElement } from "../../../api/fraudElements";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import { selectGeneratedFraudElementsForParty } from "../logic/fraudElementsSlice";
import {
  createFraudElements,
  discardGeneratedFraudElements,
} from "../logic/fraudElementsThunk";
import AIFraudElementCard from "./AIFraudElementCard";
import { getStakeholderById } from "../../stakeholder/logic/stakeholderSlice";
import WideSidePeek from "../../../components/ui/sidepeek/WideSidePeek";
import RedConfirmModal from "../../../components/ui/modals/RedConfirmModal";

interface AIFraudElementsSidePeekProps {
  partyId: string;
  onClose: () => void;
}

const AIFraudElementsSidePeek: React.FC<AIFraudElementsSidePeekProps> = ({
  partyId,
  onClose,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const generatedFraudElements = useAppSelector((state) =>
    selectGeneratedFraudElementsForParty(state, partyId),
  );

  const stakeholder = useAppSelector((state) => getStakeholderById(state, partyId));
  const partyName = stakeholder?.name;

  const [fraudElements, setFraudElements] = useState<GeneratedFraudElement[]>(
    () => generatedFraudElements?.fraud_elements ?? [],
  );

  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const canSave =
    fraudElements.length > 0 &&
    fraudElements.every((item) => Boolean(item.fraud_element_category));

  const handleUpdateElement = (
    targetIndex: number,
    changes: Partial<GeneratedFraudElement>,
  ) =>
    setFraudElements((prev) =>
      prev.map((item, index) =>
        index === targetIndex ? { ...item, ...changes } : item,
      ),
    );

  const handleRemoveElement = (targetIndex: number) =>
    setFraudElements((prev) => prev.filter((_, index) => index !== targetIndex));

  const handleSave = async () => {
    if (!caseId || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await dispatch(
        createFraudElements({
          case_id: caseId,
          request: {
            fraud_elements: fraudElements.map(
              ({ fraud_element_category, details }) => ({
                party_id: partyId,
                fraud_element_category,
                details,
              }),
            ),
          },
        }),
      ).unwrap();
    } catch {
      showErrorToast("Failed to save fraud elements. Please try again.");
      setIsSaving(false);
      return;
    }

    showSuccessToast("Fraud Elements saved successfully");
    onClose();
  };

  const handleDiscardAll = async () => {
    if (!caseId || isDiscarding) {
      return;
    }

    setIsDiscarding(true);

    try {
      await dispatch(
        discardGeneratedFraudElements({ case_id: caseId, party_id: partyId }),
      ).unwrap();
    } catch {
      showErrorToast("Failed to discard fraud elements. Please try again.");
      setIsDiscarding(false);
      return;
    }

    showSuccessToast("AI generated fraud elements discarded");
    onClose();
  };

  return (
    <>
      <WideSidePeek
        title="AI-generated Fraud Elements"
        subtitle={partyName}
        onClose={onClose}
      >
        <div className="flex shrink-0 items-center gap-2 bg-[linear-gradient(128deg,rgba(53,81,243,0.12)_0%,rgba(75,110,227,0.12)_45.06%,rgba(0,181,226,0.12)_100%)] px-4 py-3">
          <Info className="h-4 w-4 shrink-0 text-[#263AAD]" />

          <span className="text-xs font-normal leading-4 text-[#263AAD]">
            These Fraud Elements have been generated by Canso Agents. Review, make
            any changes, and save them.
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            {fraudElements.map((element, index) => (
              <AIFraudElementCard
                key={index}
                index={index}
                fraudElement={element}
                onCategoryChange={(category) =>
                  handleUpdateElement(index, { fraud_element_category: category })
                }
                onDetailsChange={(details) =>
                  handleUpdateElement(index, { details })
                }
                onDelete={() => handleRemoveElement(index)}
              />
            ))}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#E5E5E5] bg-white px-4 py-3">
          <ButtonV2
            variant="secondary"
            size="md"
            onClick={() => setIsDiscardConfirmationOpen(true)}
            disabled={isSaving}
            className="h-9 rounded-[8px] border-[#3551F3] bg-[#EBEEFE] text-[#3551F3]"
          >
            Discard All
          </ButtonV2>

          <ButtonV2
            variant="primary"
            size="md"
            onClick={handleSave}
            loading={isSaving}
            disabled={!canSave}
            className="h-9 rounded-[8px] bg-[linear-gradient(180deg,#788AF7_0%,#3551F3_100%)] text-[#FAFAFA]"
          >
            Confirm &amp; Save All
          </ButtonV2>
        </div>
      </WideSidePeek>

      {isDiscardConfirmationOpen && (
        <RedConfirmModal
          title="Discard Fraud Elements"
          description="Do you want to discard all AI generated fraud elements for this party?"
          isLoading={isDiscarding}
          onCancel={() => setIsDiscardConfirmationOpen(false)}
          onDiscard={handleDiscardAll}
        />
      )}
    </>
  );
};

export default AIFraudElementsSidePeek;